import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { customGetAPI } from "services/utils/CustomAPI";
import { provisioningModule, scopedProvisioning } from "services/utils/companyAccess";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { designWeights } from "services/constants/designTokens";
import { EmptyState } from "components/CustomComponents/EmptyState";
import { actionCentreRegistry } from "./ActionCentre/registry";
import { dashboard } from "./dashboardStyles";

// Approver status elsewhere in the app (e.g. pages/TeamZone/Approve/Leave/index.js)
// is decided by checkEditorPermission(userScreenAccess.module_details, "Approvals", "<screen>").
// That store value is scoped to whichever product the user last navigated into
// (see App.js getUserModuleAccess / product/access/provisioning?module=<product>),
// so on this landing page it still holds "My Workspace" access, not "Team
// Workspace", for anyone who hasn't opened Team Workspace yet this session. We
// fetch the same endpoint here scoped to "Team Workspace" instead of relying
// on the (possibly stale) store value, so the card doesn't wrongly hide itself
// for approvers on a fresh login.
//
// One provisioning call covers every registry entry's approve permission check -
// module_details for "Team Workspace" already contains every submodule
// (Leaves/Overtime/Off Day Duty/WorkAdjustment/Advance Salary), so the fix QW-39
// applied for Leave generalizes to all five without a second network call.
//
// Each registry entry with an `approval` block is checked independently
// (checkEditorPermission against that entry's own `screen` name) so the card
// reflects per-type approver status - e.g. an Overtime approver who isn't a
// Leave approver still sees Overtime items, not an all-or-nothing toggle.
export const PendingApprovalsSection = () => {
  const { domain, userDetails, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer,
  );
  const history = useHistory();
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [approvableEntries, setApprovableEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]);

  useEffect(() => {
    const fetchApproverAccess = async () => {
      setCheckingAccess(true);
      const res = await customGetAPI(
        `${domain}product/access/provisioning/?company_id=${
          topbarCompanyId
        }&module=${encodeURIComponent(provisioningModule(topbarCompanyId, "Team Workspace"))}`,
      );
      const moduleDetails = Array.isArray(res?.responseDetails) && provisioningModule(topbarCompanyId, "Team Workspace") !== "All"
        ? res.responseDetails
        : scopedProvisioning(res?.responseDetails, topbarCompanyId, "Team Workspace").module_details;
      setApprovableEntries(
        actionCentreRegistry.filter(
          (entry) =>
            entry.approval &&
            checkEditorPermission(moduleDetails, "Approvals", entry.approval.screen),
        ),
      );
      setCheckingAccess(false);
    };
    if (userDetails?.id) fetchApproverAccess();
  }, [domain, userDetails?.id, topbarCompanyId]);

  useEffect(() => {
    if (checkingAccess) return;
    if (approvableEntries.length === 0) {
      setPending([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchPendingApprovals = async () => {
      setLoading(true);
      const results = await Promise.all(
        approvableEntries.map(async (entry) => {
          try {
            const items = await entry.approval.fetchPendingItems({
              domain,
              userDetails,
              topbarCompanyId,
            });
            return items.map((item) => ({
              ...item,
              viewAllPath: entry.approval.viewAllPath,
            }));
          } catch (error) {
            console.error(
              `[PendingApprovals] failed to load ${entry.type}:`,
              error,
            );
            return [];
          }
        }),
      );
      if (!cancelled) {
        setPending(results.flat());
        setLoading(false);
      }
    };
    fetchPendingApprovals();
    return () => {
      cancelled = true;
    };
  }, [domain, userDetails, topbarCompanyId, checkingAccess, approvableEntries]);

  if (checkingAccess || approvableEntries.length === 0) return null;

  return (
    <Box sx={dashboard.cardSx}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Typography variant="h6" fontWeight={designWeights.heading}>
          Awaiting My Approval
        </Typography>
        <Typography
          variant="body2"
          sx={{ cursor: "pointer", color: "primary.main", fontWeight: designWeights.semibold }}
          onClick={() => history.push("/admin/teamzone/approve")}
        >
          View all
        </Typography>
      </Stack>
      {loading ? (
        <CircularProgress size={24} />
      ) : pending.length === 0 ? (
        <EmptyState subtitle="Nothing awaiting your approval." size={96} sx={{ py: 2 }} />
      ) : (
        <Stack spacing={1}>
          {pending.slice(0, 5).map((item, idx) => (
            <Stack
              key={item.id ?? idx}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                border: "1px solid #eee",
                borderRadius: 2,
                p: 1,
                cursor: "pointer",
              }}
              onClick={() => history.push(item.viewAllPath)}
            >
              <Box>
                <Typography fontWeight={designWeights.medium}>{item.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.subtitle}
                </Typography>
              </Box>
              <Chip label={item.status} size="small" color="warning" />
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
};
